import { StatusChip } from "$components/chip";
import { makeStyles } from "$components/mui-styles";
import { Box } from "@material-ui/core";
import type { ComponentProps } from "react";
import { defineMessage, useIntl } from "react-intl";
import type { CurrentVisitInsurance, PatientInsurance } from "./types";

export enum InsuranceStatus {
  VALID = "valid",
  CHECK_PENDING = "check_pending",
  INVALID = "invalid",
}

export interface HeaderInsuranceStatusProps {
  readonly insurance: PatientInsurance;
  readonly currentVisitInsurances?: ReadonlyArray<CurrentVisitInsurance>;
}

const insuranceStatusLabelMap = {
  [InsuranceStatus.VALID]: defineMessage({
    id: "patientDetails.header.insuranceStatus.valid",
    defaultMessage: "Valid",
  }),
  [InsuranceStatus.INVALID]: defineMessage({
    id: "patientDetails.header.insuranceStatus.invalid",
    defaultMessage: "Invalid",
  }),
  [InsuranceStatus.CHECK_PENDING]: defineMessage({
    id: "patientDetails.header.insuranceStatus.checkPending",
    defaultMessage: "Unknown",
  }),
};

interface InsuranceValidityChipStylesProps {
  status: InsuranceStatus;
}
const _useInsuranceValidityChipStyles = makeStyles((theme) => {
  const colors = {
    [InsuranceStatus.VALID]: theme.palette.success,
    [InsuranceStatus.CHECK_PENDING]: theme.palette.disabled,
    [InsuranceStatus.INVALID]: theme.palette.error,
  };

  return {
    chip: {
      color: ({ status }: InsuranceValidityChipStylesProps) =>
        colors[status]?.contrastText,
      backgroundColor: ({ status }: InsuranceValidityChipStylesProps) =>
        colors[status]?.main,
    },
  };
});

const getInsuranceProviderForVisit = (
  insurance: PatientInsurance,
  currentVisitInsurances?: ReadonlyArray<CurrentVisitInsurance>,
) =>
  (currentVisitInsurances ?? [])
    .filter(
      (i) =>
        i?.insurance?.insuranceProvider?.insuranceType ===
        insurance?.insuranceType,
    )
    .pop();

export function getChipStatus({
  insurance,
  currentVisitInsurances,
}: HeaderInsuranceStatusProps) {
  // Valid if:
  //   - Patient's insurance provider does not require visit validation;
  // Always check provider in case the provider introduced visit validation after patient
  // had insurance assigned to them
  if (!insurance.insuranceProvider?.visitValidation) {
    return InsuranceStatus.VALID;
  }

  // It is possible a patient has had multiple insurance policies or
  // multiple states of that same insurance during the same visit
  // so, get the most recent (last in the array) to determine current status
  const visitInsurance = getInsuranceProviderForVisit(
    insurance,
    currentVisitInsurances,
  );

  const notYetValidated =
    !visitInsurance?.visitInsuranceDetails && !visitInsurance?.isValidForVisit;

  if (!currentVisitInsurances || notYetValidated) {
    // Check pending if:
    //   - Patient's insurance does require visit validation,
    // and no current visit is present or not validation details have been recorded for visit
    return InsuranceStatus.CHECK_PENDING;
  }

  // Valid or invalid depending on what was recorded during this visit
  return visitInsurance?.isValidForVisit
    ? InsuranceStatus.VALID
    : InsuranceStatus.INVALID;
}

const INSURANCE_VALIDITY_CHIP_STATUS: Record<
  InsuranceStatus,
  ComponentProps<typeof StatusChip>["status"]
> = {
  [InsuranceStatus.VALID]: "valid",
  [InsuranceStatus.INVALID]: "invalid",
  [InsuranceStatus.CHECK_PENDING]: "disabled",
};

export const InsuranceValidityChip: FC<HeaderInsuranceStatusProps> = ({
  insurance,
  currentVisitInsurances,
}) => {
  const { formatMessage } = useIntl();
  const chipStatus = getChipStatus({ insurance, currentVisitInsurances });

  return (
    <StatusChip
      data-testid="insurance-chip"
      label={formatMessage(insuranceStatusLabelMap[chipStatus])}
      status={INSURANCE_VALIDITY_CHIP_STATUS[chipStatus]}
    />
  );
};

const useHeaderInsuranceStatusStyles = makeStyles((theme) => ({
  name: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * Given `insurance` (policy) and `currentVisit` (containing an `insuranceList` array),
 * return the first insurance provider in the visit's `insuranceList` array, else `insurance`
 *
 * This is a temporary measure until we fully support multiple insurance providers for a patient
 */
export const getCurrentInsuranceProviderName = ({
  insurance,
  currentVisitInsurances,
}: HeaderInsuranceStatusProps) => {
  const currentInsurance = getInsuranceProviderForVisit(
    insurance,
    currentVisitInsurances,
  );
  return (
    currentInsurance?.insurance?.insuranceProvider?.name ??
    insurance?.insuranceProvider?.name
  );
};

export const HeaderInsuranceStatus: FC<HeaderInsuranceStatusProps> = ({
  insurance,
  currentVisitInsurances,
}) => {
  const classes = useHeaderInsuranceStatusStyles();
  const insuranceProvider = getCurrentInsuranceProviderName({
    insurance,
    currentVisitInsurances,
  });
  if (!insuranceProvider) return null;

  return (
    <Box display="inline-flex" alignItems="center" justifyContent="flex-end">
      <span data-testid="insurance.type.name" className={classes.name}>
        {insuranceProvider}
      </span>
      <InsuranceValidityChip
        insurance={insurance}
        currentVisitInsurances={currentVisitInsurances}
      />
    </Box>
  );
};
